import { useEffect, useState } from "react";
import NumberCounter from "../../Helpers/NumberCounter";
import { cn } from "@/lib/utils";

import { Button } from "@/shadcnComponents/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcnComponents/ui/tooltip";

import { ArrowRight, InfoIcon, LoaderIcon } from "lucide-react";
import {
  Sparkles,
  Users,
  UserPlus,
  IndianRupeeIcon as Rupee,
  RefreshCw,
  Megaphone,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/shadcnComponents/ui/card";
import { ToolTipInfo } from "../Components";
import { Separator } from "@/shadcnComponents/ui/separator";
import { useNewCampaign } from "../../CustomHooks/NewCampaignHooks";
import mixpanel from "mixpanel-browser";

export const PreviewCardItem = ({
  icon,
  itemValue,
  itemSecondValue,
  itemHead,
  bg,
  isAnimate,
  interval,
  prevNumber,
  itemClassName,
  iconClassName,
  infoContent,
}: {
  icon: React.ReactNode;
  itemValue: string | string[];
  itemSecondValue?: number;
  itemHead: string;
  bg: string;
  isAnimate: boolean;
  interval?: number;
  prevNumber?: number;
  itemClassName?: string;
  iconClassName?: string;
  infoContent?: React.ReactNode | string;
}) => {
  return (
    <>
      <div className="flex justify-between items-end self-stretch w-full bg-[color:var(--Shade-White,#FFF)] shadow-[2px_2px_8px_0px_rgba(0,0,0,0.12)] p-3 rounded-md">
        <div className="flex flex-col justify-center items-start gap-2 flex-[1_0_0]">
          <div className="flex justify-between items-center self-stretch">
            <div className="flex items-center gap-1">
              <div
                className={cn(
                  `flex w-[24px] h-[24px] justify-center items-center p-[5px] rounded-[4px] ${bg}`,
                  iconClassName
                )}
              >
                {icon}
              </div>
              <span className="text-[#898989] text-xs not-italic font-normal leading-[normal]">
                {itemHead}
              </span>
            </div>

            <div className="flex">
              {infoContent && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <InfoIcon
                        size={12}
                        color="#4C4F52"
                        className="cursor-pointer"
                      />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-sm text-wrap">
                      <p>{infoContent}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
          <div className="flex flex-wrap flex-col justify-center items-start gap-1">
            <span
              className={cn(
                "text-[color:var(--Shade-Black,#000)] text-[22px] flex gap-1 not-italic font-bold leading-[normal]",
                itemClassName
              )}
            >
              {isAnimate ? (
                <NumberCounter
                  className="text-[22px]"
                  prevNumber={prevNumber || 0}
                  targetNumber={
                    parseInt(
                      Array.isArray(itemValue) ? itemValue.join("") : itemValue
                    ) || 0
                  }
                  interval={interval}
                />
              ) : Array.isArray(itemValue) ? (
                itemValue.join(",")
              ) : (
                itemValue
              )}
              {itemSecondValue > 0 && `- ${itemSecondValue?.toLocaleString()}`}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export const PreviewCard = () => {
  const {
    previewData,
    currentStep,
    formData,
    selectedCreators,
    setRefreshLoading,
    handleSaveData,
    refreshLoading,
  } = useNewCampaign();

  const minImpressions =
    previewData?.ExpectedFinalMinImpression || previewData?.minImpressions;

  const maxImpressions =
    previewData?.ExpectedFinalMaxImpression || previewData?.maxImpressions;

  const budget =
    currentStep === 3
      ? previewData?.ExpectedSpendAmount
      : previewData?.ExpectedSpendAmount || formData?.Budget?.maxBillAmount;

  const numberOfInfluencers =
    currentStep < 3
      ? previewData?.numberOfInfluencers
      : selectedCreators?.length;

  const BudgetText =
    currentStep >= 3
      ? formData?.campaignInfo?.collabType === "fixed"
        ? "Total Expense"
        : "Expected Expense"
      : "Budget";

  const InfluencerText =
    currentStep === 3 ? "Selected Influencers" : "No. of Influencers";

  const handleRefresh = async () => {
    mixpanel.track(
      "Brand Draft Campaign:select Influencer section : Refresh - clicked"
    );
    setRefreshLoading(true);
    await handleSaveData({
      isAddMore: false,
      isRefresh: true,
    });
    setRefreshLoading(false);
  };

  const [, setPrevImpression] = useState<number>(minImpressions);
  const [, setPrevMaxImpression] = useState<number>(maxImpressions);
  const [, setPrevInfluencer] = useState<number>(numberOfInfluencers);

  useEffect(() => {
    setPrevImpression(minImpressions);
    setPrevInfluencer(numberOfInfluencers);
    setPrevMaxImpression(maxImpressions);
  }, [minImpressions, numberOfInfluencers, maxImpressions]);

  return (
    <>
      <Card className="p-4 bg-gradient-to-br from-white-50 to-white-100 border-gray-200">
        <CardHeader className="p-0 pb-2 gap-[2px] ">
          <CardTitle className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-yellow-500" />
            Campaign Outline
          </CardTitle>
          <CardDescription className="text-[10px] text-gray-600">
            A quick overview of the main numbers & ROI
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0 pt-2 space-y-4 text-sm">
          {maxImpressions > 0 && (
            <div className="border rounded-lg p-3 bg-[#FBFCFF] gap-3 flex flex-col  ">
              <div className="flex flex-col gap-[2px] ">
                <h3 className="font-semibold text-[#1F1F1F] text-xs flex items-center gap-2">
                  <Users className="h-4 w-4 text-red-500" />
                  Estimated Reach
                  <ToolTipInfo
                    size={12}
                    Content={
                      <div>
                        This is the projected number of people who will see your
                        campaign and does not include repeat displays. It's
                        based on various factors and serves as an estimate to
                        provide visibility.
                      </div>
                    }
                  />
                </h3>
                <p className="text-[10px] text-gray-600">
                  The no. of people whom this campaign will reach
                </p>
              </div>
              <Separator />
              <p className="text-xl font-bold text-gray-900">
                {parseInt(minImpressions?.toString())?.toLocaleString()}-
                {parseInt(maxImpressions?.toString())?.toLocaleString()}
              </p>
            </div>
          )}

          {numberOfInfluencers > 0 && (
            <div className="border rounded-lg p-3 bg-[#FBFCFF] gap-3 flex flex-col ">
              <div className="flex flex-col gap-[2px] ">
                <h3 className="font-semibold text-[#1F1F1F] text-xs flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-red-500" />
                  {InfluencerText}
                </h3>
                <p className="text-[10px] text-gray-600">
                  {currentStep < 3
                    ? "Estimated number within the set constraints"
                    : "Number of influencers shortlisted"}
                </p>
              </div>
              <Separator />
              <p className="text-xl font-bold text-gray-900">
                {numberOfInfluencers}
              </p>

              {/* <div className="flex gap-2 mt-1">
              <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                2: Micro
              </span>
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                3: Macro
              </span>
              <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                4: Nano
              </span>
            </div> */}
            </div>
          )}

          {budget > 0 && (
            <div className="border rounded-lg p-3 bg-[#FBFCFF] gap-3 flex flex-col ">
              <div className="flex flex-col gap-[2px] ">
                <h3 className="font-semibold text-[#1F1F1F] text-xs flex items-center gap-2">
                  <Rupee className="h-4 w-4 text-red-500" />
                  {BudgetText}
                  <ToolTipInfo
                    size={12}
                    Content={
                      BudgetText === "Budget" ? (
                        <div>
                          This is the maximum amount you're willing to spend on
                          this campaign.Adjust it to explore a broader or more
                          focused selection of influencers.
                        </div>
                      ) : formData?.campaignInfo?.collabType === "fixed" ? (
                        <div>
                          This represents the overall expense calculated from
                          the commercials of the chosen influencers - whether
                          recommended or custom.
                        </div>
                      ) : (
                        <div>
                          This represents the anticipated expense, calculated
                          from the performance history and expected outcomes of
                          the chosen influencers. The final budget utilization
                          may be lower, as per actual outcomes.
                        </div>
                      )
                    }
                  />
                </h3>
                <p className="text-[10px] text-gray-600">
                  {currentStep < 3
                    ? "Maximum set amount for this campaign"
                    : "Estimated expenditure based on of the influencers chosen"}
                </p>
              </div>
              <Separator />
              <div className="flex flex-col gap-1">
                <p className="text-xl font-bold text-gray-900">
                  ₹{parseFloat(budget?.toFixed(2))?.toLocaleString()}
                </p>
                <p className="text-[10px] text-gray-500 italic">
                  Will vary based on influencer selection
                </p>
              </div>
            </div>
          )}

          <div className="p-3">
            <Button
              onClick={() => handleRefresh()}
              className="w-full text-xs bg-red-600 hover:bg-red-700 text-white"
              size="sm"
            >
              {refreshLoading ? (
                <LoaderIcon className="animate-spin " />
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Check Latest Outline
                </>
              )}
            </Button>
            <p className="text-[10px] italic text-gray-600 mb-2">
              Refresh to see the latest outline before proceeding
            </p>
          </div>

          <div className="hidden border rounded-lg p-3 bg-[#FBFCFF] gap-3  flex-col  ">
            <div className="flex flex-col gap-[2px] ">
              <h3 className="font-semibold text-[#1F1F1F] text-xs flex items-center gap-2">
                <Megaphone className="h-4 w-4 text-red-500" />
                Estimated Impact
              </h3>
            </div>
            <Separator />

            <ul className="text-xs text-gray-600 space-y-1">
              <li className="flex items-center gap-1">
                <ArrowRight className="h-3 w-3 flex-shrink-0" />
                <span>Increased brand awareness</span>
              </li>
              <li className="flex items-center gap-1">
                <ArrowRight className="h-3 w-3 flex-shrink-0" />
                <span>Higher engagement rates</span>
              </li>
              <li className="flex items-center gap-1">
                <ArrowRight className="h-3 w-3 flex-shrink-0" />
                <span>Potential lead generation</span>
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </>
  );
};
